import { RevenueMarketSelect, parseRevenueMarket, type RevenueMarket } from '@/components/travel/revenue-market';
import { DirectionSelect, parseDirection, type Direction } from '@/components/travel/direction';
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Loader2,
  Search,
  UserPlus,
  Hotel,
  Bus,
  Plane,
  Users,
  ChevronDown,
  ChevronRight,
  UserCheck,
  Receipt,
  StickyNote,
  Layers,
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Combobox } from '@/components/ui/combobox';
import { CustomerDrawer } from '@/components/travel/customers/customer-drawer';
import {
  AllocationPickerModal,
  SelectedAllocation,
} from '@/components/travel/bookings/allocation-picker-modal';
import { BookingItemRow, CURRENCIES } from '@/components/travel/bookings/booking-item-row';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { PaymentAccountSelect } from '@/components/travel/accounting/payment-account-select';
import { cn } from '@/lib/utils';

interface PassengerForm {
  title?: string;
  firstName: string;
  lastName: string;
  dateOfBirth?: string;
  nationality?: string;
  passportNumber?: string;
  passportExpiry?: string;
  passengerType: 'ADULT' | 'CHILD' | 'INFANT';
  isLeadPassenger: boolean;
}

interface BookingItemForm {
  serviceType:
    | 'ACCOMMODATION'
    | 'TRANSPORT'
    | 'ACTIVITY'
    | 'MEAL'
    | 'GUIDE'
    | 'INSURANCE'
    | 'TRANSFER'
    | 'OTHER';
  name: string;
  description?: string;
  serviceDate?: string;
  endDate?: string;
  quantity: number;
  unitCost: number;
  sellingPrice?: number;
  serviceFee: number;
  currency: string;
  passengerIndexes: number[];
  fundingSourceId?: string;
  partnerId?: string;
  paymentType?: 'COMPANY_ACCOUNT' | 'PARTNER_CREDIT' | 'CUSTOMER_CARD';
  paymentAccountId?: string;
  tripType?: 'one_way' | 'round_trip';
  fromAirport?: string;
  toAirport?: string;
  flightNumber?: string;
  departureTime?: string;
  returnFlightNumber?: string;
  returnDepartureTime?: string;
  pickupLocation?: string;
  dropoffLocation?: string;
  pickupTime?: string;
  vehicleType?: string;
}

interface BookingForm {
  customerId?: string;
  packageId?: string;
  travelStartDate: string;
  travelEndDate: string;
  specialRequests?: string;
  internalNotes?: string;
  passengers: PassengerForm[];
  items: BookingItemForm[];
  initialPaymentAmount?: number;
  initialPaymentMethod?:
    | 'CASH'
    | 'CREDIT_CARD'
    | 'DEBIT_CARD'
    | 'BANK_TRANSFER'
    | 'PAYPAL'
    | 'CHEQUE'
    | 'OTHER';
  initialPaymentAccountId?: string;
  createInvoice: boolean;
  invoiceType?: 'PROFORMA' | 'INVOICE' | 'CREDIT_NOTE';
  invoiceDueDate?: string;
}

// Section header with icon
function SectionTitle({
  icon: Icon,
  children,
  count,
  action,
}: {
  icon: React.ElementType;
  children: React.ReactNode;
  count?: number;
  action?: React.ReactNode;
}) {
  return (
    <CardHeader className="p-4 pb-0">
      <div className="flex items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <div className="bg-primary/10 flex h-7 w-7 items-center justify-center rounded-md">
            <Icon className="text-primary h-3.5 w-3.5" />
          </div>
          {children}
          {count !== undefined && (
            <Badge
              variant="secondary"
              className="ml-1 h-5 min-w-5 justify-center rounded-full px-1.5 text-[11px] font-medium"
            >
              {count}
            </Badge>
          )}
        </CardTitle>
        {action}
      </div>
    </CardHeader>
  );
}

// Field wrapper with proper label sizing
function Field({
  label,
  required,
  children,
  className,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label className="text-muted-foreground text-xs font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
    </div>
  );
}

export function NewBookingPage() {
  const { t } = useTranslation('bookings');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [revenueMarket, setRevenueMarket] = useState<RevenueMarket | undefined>();
  const [direction, setDirection] = useState<Direction | undefined>(parseDirection(searchParams.get('direction')));
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  } | null>(null);
  const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<{
    id: string;
    name: string;
    code: string;
    duration: number;
  } | null>(null);
  const [selectedAllocations, setSelectedAllocations] = useState<SelectedAllocation[]>([]);
  const [isAllocationPickerOpen, setIsAllocationPickerOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    allocations: true,
    notes: false,
  });
  const [isStickyExpanded, setIsStickyExpanded] = useState(false);
  const [expandedPassengers, setExpandedPassengers] = useState<Record<number, boolean>>({});

  const form = useForm<BookingForm>({
    defaultValues: {
      travelStartDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      travelEndDate: format(addDays(new Date(), 37), 'yyyy-MM-dd'),
      passengers: [{ firstName: '', lastName: '', passengerType: 'ADULT', isLeadPassenger: true }],
      items: [],
      createInvoice: false,
      invoiceType: 'PROFORMA',
    },
  });

  const { register, handleSubmit, control, watch, setValue } = form;

  const {
    fields: passengers,
    append: appendPassenger,
    remove: removePassenger,
  } = useFieldArray({
    control,
    name: 'passengers',
  });

  const {
    fields: items,
    append: appendItem,
    remove: removeItem,
  } = useFieldArray({
    control,
    name: 'items',
  });

  // Queries
  const { data: customersData } = trpc.customer.list.useQuery(
    { search: customerSearch },
    { enabled: customerSearch.length >= 2 }
  );

  const { data: packagesData } = trpc.package.list.useQuery({});

  const { data: paymentAccounts } = trpc.account.listPaymentAccounts.useQuery();

  const { data: packageAllocConfigs } = trpc.packageAllocationConfig.listByPackage.useQuery(
    selectedPackage?.id || '',
    { enabled: !!selectedPackage?.id }
  );

  // Mutations
  const createBookingMutation = trpc.booking.createFull.useMutation({
    onSuccess: (data) => {
      showSuccessToast(t('new.bookingCreated'));
      if (data?.id) {
        navigate(`/travel/bookings/${data.id}`);
      } else {
        navigate('/travel/bookings');
      }
    },
    onError: (error) => {
      showErrorToast(error.message || t('new.failedToCreate'));
    },
  });

  const watchedValues = watch();
  const totalAdults = passengers.filter(
    (p) => watchedValues.passengers?.[passengers.indexOf(p)]?.passengerType === 'ADULT'
  ).length;
  const totalChildren = passengers.filter(
    (p) => watchedValues.passengers?.[passengers.indexOf(p)]?.passengerType === 'CHILD'
  ).length;
  const totalInfants = passengers.filter(
    (p) => watchedValues.passengers?.[passengers.indexOf(p)]?.passengerType === 'INFANT'
  ).length;

  const itemsByCurrency = (watchedValues.items || []).reduce(
    (acc, item) => {
      const currency = item.currency || 'TRY';
      const quantity = item.quantity || 1;
      const unitCost = item.unitCost || 0;
      const serviceFee = item.serviceFee || 0;
      const sellingPrice = item.sellingPrice ?? unitCost + serviceFee;

      if (!acc[currency]) {
        acc[currency] = { cost: 0, sell: 0, profit: 0 };
      }
      acc[currency].cost += unitCost * quantity;
      acc[currency].sell += sellingPrice * quantity;
      acc[currency].profit += serviceFee * quantity;
      return acc;
    },
    {} as Record<string, { cost: number; sell: number; profit: number }>
  );

  const itemSummaries = (watchedValues.items || []).map((item, idx) => {
    const quantity = item.quantity || 1;
    const unitCost = item.unitCost || 0;
    const serviceFee = item.serviceFee || 0;
    const sellingPrice = item.sellingPrice ?? unitCost + serviceFee;
    const currency = item.currency || 'TRY';
    const currencySymbol = CURRENCIES.find((c) => c.value === currency)?.symbol || '';

    return {
      index: idx,
      name: item.name,
      serviceType: item.serviceType || 'OTHER',
      quantity,
      cost: unitCost * quantity,
      sell: sellingPrice * quantity,
      profit: serviceFee * quantity,
      currency,
      currencySymbol,
    };
  });

  const itemsTotal = Object.values(itemsByCurrency).reduce((sum, amounts) => sum + amounts.sell, 0);
  const itemsProfitTotal = Object.values(itemsByCurrency).reduce(
    (sum, amounts) => sum + amounts.profit,
    0
  );

  const allocationsTotal = selectedAllocations.reduce(
    (sum, alloc) => sum + alloc.unitCost * alloc.units,
    0
  );

  const grandTotal = itemsTotal + allocationsTotal;

  // Load package allocations when package is selected
  useEffect(() => {
    if (packageAllocConfigs && packageAllocConfigs.length > 0) {
      const packageAllocations: SelectedAllocation[] = packageAllocConfigs.map((config) => {
        const startDate = new Date(watchedValues.travelStartDate);
        const serviceDate = new Date(startDate);
        serviceDate.setDate(serviceDate.getDate() + (config.dayOffset || 0));

        return {
          id: crypto.randomUUID(),
          allocationId: config.allocation.id,
          name: config.allocation.name,
          allocationType: config.allocation.allocationType,
          partnerId: config.allocation.partner.id,
          partnerName: config.allocation.partner.companyName,
          units: config.unitsPerBooking,
          unitCost: config.allocation.unitCost ?? 0,
          currency: config.allocation.currency ?? 'USD',
          serviceDate: format(serviceDate, 'yyyy-MM-dd'),
          availableUnits: config.allocation.availableUnits,
          fromPackage: true,
          dayOffset: config.dayOffset,
        };
      });
      setSelectedAllocations(packageAllocations);
    } else if (!selectedPackage) {
      setSelectedAllocations((prev) => prev.filter((a) => !a.fromPackage));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packageAllocConfigs, selectedPackage, watchedValues.travelStartDate]);

  const handleCustomerSelect = (customer: typeof selectedCustomer) => {
    setSelectedCustomer(customer);
    if (customer) {
      setValue('customerId', customer.id);
      const currentFirstName = form.getValues('passengers.0.firstName');
      if (passengers.length > 0 && !currentFirstName) {
        setValue('passengers.0.firstName', customer.firstName, { shouldDirty: true });
        setValue('passengers.0.lastName', customer.lastName, { shouldDirty: true });
      }
    }
  };

  const handlePackageSelect = (pkgId: string) => {
    const pkg = packagesData?.packages.find((p) => p.id === pkgId);
    if (pkg) {
      setSelectedPackage({
        id: pkg.id,
        name: pkg.name,
        code: pkg.code,
        duration: pkg.duration,
      });
      setValue('packageId', pkg.id);
      const startDate = new Date(watchedValues.travelStartDate);
      if (!isNaN(startDate.getTime())) {
        setValue('travelEndDate', format(addDays(startDate, pkg.duration - 1), 'yyyy-MM-dd'));
      }
    }
  };

  const handleCustomerCreated = (customer: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string;
  }) => {
    handleCustomerSelect({ ...customer, email: customer.email ?? '' });
  };

  const onSubmit = (data: BookingForm) => {
    if (!selectedCustomer) {
      showErrorToast(t('new.selectCustomer'));
      return;
    }

    const formData = {
      customerId: selectedCustomer.id,
      direction,
      revenueMarket,
      packageId: selectedPackage?.id,
      travelStartDate: new Date(data.travelStartDate),
      travelEndDate: new Date(data.travelEndDate),
      totalAdults,
      totalChildren,
      totalInfants,
      currency: 'TRY',
      specialRequests: data.specialRequests,
      internalNotes: data.internalNotes,
      passengers: data.passengers
        .filter((p) => p.firstName && p.lastName)
        .map((p) => ({
          title: p.title,
          firstName: p.firstName,
          lastName: p.lastName,
          dateOfBirth: p.dateOfBirth ? new Date(p.dateOfBirth) : undefined,
          nationality: p.nationality,
          passportNumber: p.passportNumber,
          passportExpiry: p.passportExpiry ? new Date(p.passportExpiry) : undefined,
          passengerType: p.passengerType,
          isLeadPassenger: p.isLeadPassenger,
        })),
      items: data.items
        .filter((item) => item.name)
        .map((item) => ({
          serviceType: item.serviceType,
          name: item.name,
          description: item.description,
          serviceDate: item.serviceDate ? new Date(item.serviceDate) : undefined,
          endDate: item.endDate ? new Date(item.endDate) : undefined,
          quantity: item.quantity,
          unitCost: item.unitCost,
          sellingPrice: (item.unitCost || 0) + (item.serviceFee || 0),
          currency: item.currency || 'TRY',
          partnerId: item.partnerId || undefined,
          passengerIndexes: item.passengerIndexes,
          fundingSourceId:
            item.fundingSourceId && !item.fundingSourceId.startsWith('account:')
              ? item.fundingSourceId
              : undefined,
          supplierPaymentType: item.paymentType || undefined,
          supplierPaymentAccountId: item.paymentAccountId || undefined,
        })),
      totalAmount: grandTotal || undefined,
      initialPayment:
        data.initialPaymentAmount && data.initialPaymentMethod
          ? {
              amount: data.initialPaymentAmount,
              paymentMethod: data.initialPaymentMethod,
              accountId: data.initialPaymentAccountId || undefined,
            }
          : undefined,
      manualAllocations:
        selectedAllocations.length > 0
          ? selectedAllocations.map((alloc) => ({
              allocationId: alloc.allocationId,
              units: alloc.units,
              serviceDate: new Date(alloc.serviceDate),
              endDate: alloc.endDate ? new Date(alloc.endDate) : undefined,
            }))
          : undefined,
      autoConsumeAllocations: selectedAllocations.length === 0,
      invoice: data.createInvoice
        ? {
            create: true,
            type: data.invoiceType || 'PROFORMA',
            dueDate: data.invoiceDueDate ? new Date(data.invoiceDueDate) : undefined,
          }
        : undefined,
    };

    createBookingMutation.mutate(formData);
  };

  const handleAddAllocations = (newAllocations: SelectedAllocation[]) => {
    setSelectedAllocations((prev) => [...prev, ...newAllocations]);
  };

  const handleRemoveAllocation = (id: string) => {
    setSelectedAllocations((prev) => prev.filter((a) => a.id !== id));
  };

  const handleUpdateAllocationUnits = (id: string, units: number) => {
    setSelectedAllocations((prev) =>
      prev.map((a) =>
        a.id === id ? { ...a, units: Math.max(1, Math.min(units, a.availableUnits)) } : a
      )
    );
  };

  const handleUpdateAllocationDate = (id: string, serviceDate: string) => {
    setSelectedAllocations((prev) => prev.map((a) => (a.id === id ? { ...a, serviceDate } : a)));
  };

  const allocationTypeIcons: Record<string, typeof Hotel> = {
    HOTEL_ROOM: Hotel,
    TOUR_SEAT: Users,
    TRANSFER_VEHICLE: Bus,
    FLIGHT_SEAT: Plane,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link
            to="/travel/bookings"
            className="bg-background text-muted-foreground hover:bg-accent hover:text-foreground inline-flex h-8 w-8 items-center justify-center rounded-md border shadow-sm transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{t('new.title')}</h1>
            <p className="text-muted-foreground text-sm">
              {selectedCustomer
                ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}`
                : t('new.selectCustomer')}
            </p>
          </div>
        </div>
      </div>

      <form className="space-y-6">
        <DirectionSelect value={direction} onChange={value => setDirection(parseDirection(value))} />
        <RevenueMarketSelect value={revenueMarket} onChange={value => setRevenueMarket(parseRevenueMarket(value))} />
        <div className="space-y-6">
          {/* Reservation Info Card — Customer + Package + Dates */}
          <Card>
            <SectionTitle icon={UserCheck}>{t('new.customer')}</SectionTitle>
            <CardContent className="space-y-5 p-4">
              {/* Customer */}
              {selectedCustomer ? (
                <div className="bg-muted/50 flex items-center gap-3 rounded-lg border p-3">
                  <div className="bg-primary/10 text-primary flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold">
                    {selectedCustomer.firstName[0]}
                    {selectedCustomer.lastName[0]}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {selectedCustomer.firstName} {selectedCustomer.lastName}
                    </p>
                    <p className="text-muted-foreground truncate text-xs">
                      {selectedCustomer.email}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0 text-xs"
                    onClick={() => {
                      setSelectedCustomer(null);
                      setValue('customerId', undefined);
                    }}
                  >
                    {t('new.change')}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
                    <Input
                      placeholder={t('new.searchCustomers')}
                      value={customerSearch}
                      onChange={(e) => setCustomerSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  {customerSearch.length >= 2 && customersData?.customers && (
                    <div className="max-h-32 divide-y overflow-y-auto rounded-lg border">
                      {customersData.customers.length > 0 ? (
                        customersData.customers.map((customer) => (
                          <button
                            key={customer.id}
                            type="button"
                            className="hover:bg-muted/50 flex w-full items-center gap-3 px-3 py-2 text-left transition-colors"
                            onClick={() =>
                              handleCustomerSelect({
                                id: customer.id,
                                firstName: customer.firstName,
                                lastName: customer.lastName,
                                email: customer.email ?? '',
                                phone: customer.phone ?? undefined,
                              })
                            }
                          >
                            <div className="bg-muted flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-medium">
                              {customer.firstName[0]}
                              {customer.lastName[0]}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium">
                                {customer.firstName} {customer.lastName}
                              </p>
                              <p className="text-muted-foreground truncate text-xs">
                                {customer.email}
                              </p>
                            </div>
                          </button>
                        ))
                      ) : (
                        <p className="text-muted-foreground px-3 py-2.5 text-sm">
                          {t('new.noCustomersFound')}
                        </p>
                      )}
                    </div>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() => setIsCustomerDrawerOpen(true)}
                  >
                    <UserPlus className="mr-2 h-4 w-4" />
                    {t('new.createNewCustomer')}
                  </Button>
                </div>
              )}

              {/* Package + Dates Row */}
              <div className="grid gap-4 border-t pt-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="sm:col-span-2">
                  <Field label={t('new.packageOptional')}>
                    <Combobox
                      options={
                        packagesData?.packages.map((pkg) => ({
                          value: pkg.id,
                          label: `${pkg.code} - ${pkg.name} (${pkg.duration}d)`,
                        })) ?? []
                      }
                      value={selectedPackage?.id || ''}
                      onValueChange={handlePackageSelect}
                      placeholder={t('new.selectPackage')}
                      searchPlaceholder={t('new.searchPackages')}
                    />
                  </Field>
                </div>
                <Field label={t('new.startDate')} required>
                  <Input type="date" {...register('travelStartDate', { required: true })} />
                </Field>
                <Field label={t('new.endDate')} required>
                  <Input type="date" {...register('travelEndDate', { required: true })} />
                </Field>
              </div>
            </CardContent>
          </Card>

          {/* Travellers Card */}
          <Card>
            <SectionTitle
              icon={Users}
              count={passengers.length}
              action={
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    appendPassenger({
                      firstName: '',
                      lastName: '',
                      passengerType: 'ADULT',
                      isLeadPassenger: false,
                    })
                  }
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  {t('new.addTraveller')}
                </Button>
              }
            >
              {t('new.travellers')}
            </SectionTitle>
            <CardContent className="p-4">
              <div className="space-y-3">
                {passengers.map((field, index) => {
                  const pax = watchedValues.passengers?.[index];
                  const hasName = pax?.firstName && pax?.lastName;
                  const paxType = pax?.passengerType || 'ADULT';
                  const paxTypeLabel =
                    paxType === 'ADULT'
                      ? t('new.adult')
                      : paxType === 'CHILD'
                        ? t('new.child')
                        : t('new.infant');
                  return (
                    <div
                      key={field.id}
                      className={cn(
                        'bg-card rounded-lg border transition-all duration-200',
                        expandedPassengers[index]
                          ? 'border-primary/50 ring-primary/10 shadow-sm ring-1'
                          : 'border-border hover:border-border/80 hover:shadow-sm'
                      )}
                    >
                      {/* Summary Row */}
                      <div
                        onClick={() =>
                          setExpandedPassengers((prev) => ({ ...prev, [index]: !prev[index] }))
                        }
                        className={cn(
                          'flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors',
                          !expandedPassengers[index] && 'hover:bg-muted/30'
                        )}
                      >
                        <span
                          className={cn(
                            'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                            index === 0
                              ? 'bg-primary/10 text-primary'
                              : 'bg-muted text-muted-foreground'
                          )}
                        >
                          {index + 1}
                        </span>
                        <div className="flex min-w-0 flex-1 items-center gap-2">
                          <span className="truncate text-sm font-medium">
                            {hasName
                              ? `${pax.title ? pax.title + ' ' : ''}${pax.firstName} ${pax.lastName}`
                              : `${t('new.traveller')} ${index + 1}`}
                          </span>
                          <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px]">
                            {paxTypeLabel}
                          </Badge>
                          {index === 0 && (
                            <Badge variant="outline" className="shrink-0 px-1.5 py-0 text-[10px]">
                              {t('new.lead')}
                            </Badge>
                          )}
                          {!expandedPassengers[index] && pax?.nationality && (
                            <span className="text-muted-foreground hidden text-xs sm:inline">
                              {pax.nationality}
                            </span>
                          )}
                          {!expandedPassengers[index] && pax?.passportNumber && (
                            <span className="text-muted-foreground hidden text-xs sm:inline">
                              PP: {pax.passportNumber}
                            </span>
                          )}
                        </div>
                        <ChevronDown
                          className={cn(
                            'text-muted-foreground h-4 w-4 shrink-0 transition-transform duration-200',
                            expandedPassengers[index] && 'rotate-180'
                          )}
                        />
                        {passengers.length > 1 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="text-muted-foreground hover:text-destructive h-8 w-8 shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              removePassenger(index);
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>

                      {/* Expanded Content — default open, manually toggle */}
                      {expandedPassengers[index] !== false && (
                        <div className="space-y-3 border-t px-5 pb-5 pt-4">
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                            <Field
                              label={t('new.titleField')}
                              className="sm:col-span-1 lg:col-span-1"
                            >
                              <Combobox
                                options={[
                                  { value: 'Mr', label: 'Mr' },
                                  { value: 'Mrs', label: 'Mrs' },
                                  { value: 'Ms', label: 'Ms' },
                                ]}
                                value={watchedValues.passengers[index]?.title || ''}
                                onValueChange={(v) => setValue(`passengers.${index}.title`, v)}
                                placeholder="—"
                              />
                            </Field>
                            <Field label={t('new.firstName')} required className="lg:col-span-2">
                              <Input
                                {...register(`passengers.${index}.firstName`, { required: true })}
                                placeholder={t('new.firstName')}
                              />
                            </Field>
                            <Field label={t('new.lastName')} required className="lg:col-span-2">
                              <Input
                                {...register(`passengers.${index}.lastName`, { required: true })}
                                placeholder={t('new.lastName')}
                              />
                            </Field>
                          </div>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                            <Field label={t('new.type')}>
                              <Combobox
                                options={[
                                  { value: 'ADULT', label: t('new.adult') },
                                  { value: 'CHILD', label: t('new.child') },
                                  { value: 'INFANT', label: t('new.infant') },
                                ]}
                                value={watchedValues.passengers[index]?.passengerType || 'ADULT'}
                                onValueChange={(v) =>
                                  setValue(
                                    `passengers.${index}.passengerType`,
                                    v as 'ADULT' | 'CHILD' | 'INFANT'
                                  )
                                }
                              />
                            </Field>
                            <Field label={t('new.dateOfBirth')}>
                              <Input type="date" {...register(`passengers.${index}.dateOfBirth`)} />
                            </Field>
                            <Field label={t('new.nationality')}>
                              <Input
                                {...register(`passengers.${index}.nationality`)}
                                placeholder="TR"
                              />
                            </Field>
                            <Field label={t('new.passportNo')}>
                              <Input
                                {...register(`passengers.${index}.passportNumber`)}
                                placeholder={t('new.passportNo')}
                              />
                            </Field>
                            <Field label={t('new.passportExpiry')}>
                              <Input
                                type="date"
                                {...register(`passengers.${index}.passportExpiry`)}
                              />
                            </Field>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Booking Items Card */}
          <Card>
            <SectionTitle icon={Layers} count={items.length}>
              {t('new.bookingItems')}
            </SectionTitle>
            <CardContent className="p-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-8 text-center">
                  <Layers className="text-muted-foreground/50 mb-2 h-8 w-8" />
                  <p className="text-muted-foreground text-sm">{t('new.noItemsYet')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((field, index) => (
                    <BookingItemRow
                      key={field.id}
                      index={index}
                      form={form}
                      passengers={watchedValues.passengers || []}
                      accounts={
                        paymentAccounts?.map((acc) => ({
                          id: acc.id,
                          code: acc.code,
                          name: acc.name,
                          type: acc.type,
                          subType: acc.subType,
                          balance: acc.balance ? Number(acc.balance) : undefined,
                          currency: acc.currency,
                        })) || []
                      }
                      onRemove={() => removeItem(index)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Allocations Card (Collapsible) */}
          <Collapsible
            open={expandedSections.allocations}
            onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, allocations: open }))}
          >
            <Card>
              <CollapsibleTrigger asChild>
                <div className="hover:bg-muted/30 flex cursor-pointer items-center justify-between p-4 transition-colors">
                  <div className="flex items-center gap-2">
                    <div className="bg-primary/10 flex h-7 w-7 items-center justify-center rounded-md">
                      <Hotel className="text-primary h-3.5 w-3.5" />
                    </div>
                    <span className="text-sm font-semibold">{t('new.allocations')}</span>
                    {selectedAllocations.length > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-1 h-5 min-w-5 justify-center rounded-full px-1.5 text-[11px] font-medium"
                      >
                        {selectedAllocations.length}
                      </Badge>
                    )}
                    {expandedSections.allocations ? (
                      <ChevronDown className="text-muted-foreground ml-1 h-4 w-4" />
                    ) : (
                      <ChevronRight className="text-muted-foreground ml-1 h-4 w-4" />
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsAllocationPickerOpen(true);
                    }}
                  >
                    <Plus className="mr-1.5 h-3.5 w-3.5" />
                    {t('new.add')}
                  </Button>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="border-t p-4 pt-4">
                  {selectedAllocations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-6 text-center">
                      <Hotel className="text-muted-foreground/50 mb-2 h-8 w-8" />
                      <p className="text-muted-foreground text-sm">
                        {t('new.noAllocationsSelected')}
                      </p>
                      <p className="text-muted-foreground mt-1 text-xs">
                        {t('new.selectPackageOrAdd')}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {selectedAllocations.map((alloc) => {
                        const Icon = allocationTypeIcons[alloc.allocationType] || Hotel;
                        return (
                          <div
                            key={alloc.id}
                            className="bg-muted/30 flex items-center gap-3 rounded-lg border p-3"
                          >
                            <div className="bg-background flex h-8 w-8 shrink-0 items-center justify-center rounded-md shadow-sm">
                              <Icon className="text-muted-foreground h-4 w-4" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <p className="truncate text-sm font-medium">{alloc.name}</p>
                                {alloc.fromPackage && (
                                  <Badge variant="outline" className="shrink-0 text-[11px]">
                                    {t('new.fromPackage')}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-muted-foreground truncate text-xs">
                                {alloc.partnerName}
                              </p>
                            </div>
                            <Input
                              type="number"
                              value={alloc.units}
                              onChange={(e) =>
                                handleUpdateAllocationUnits(alloc.id, parseInt(e.target.value) || 1)
                              }
                              min={1}
                              max={alloc.availableUnits}
                              className="w-16 text-center"
                            />
                            <Input
                              type="date"
                              value={alloc.serviceDate}
                              onChange={(e) => handleUpdateAllocationDate(alloc.id, e.target.value)}
                              className="w-36"
                            />
                            <div className="bg-muted/50 flex h-9 w-24 items-center justify-end rounded-md border px-3 text-sm font-medium tabular-nums">
                              {new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: alloc.currency,
                                minimumFractionDigits: 0,
                              }).format(alloc.unitCost * alloc.units)}
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="text-muted-foreground hover:text-destructive h-8 w-8 shrink-0"
                              onClick={() => handleRemoveAllocation(alloc.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        );
                      })}

                      {/* Allocations Total */}
                      <div className="bg-muted/50 flex justify-end gap-4 rounded-lg px-4 py-2.5">
                        <span className="text-muted-foreground text-xs">
                          {t('new.allocationsTotal')}
                        </span>
                        <span className="text-sm font-semibold tabular-nums">
                          {new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'TRY',
                          }).format(allocationsTotal)}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>

          {/* Notes Card (Collapsible) */}
          <Collapsible
            open={expandedSections.notes}
            onOpenChange={(open) => setExpandedSections((prev) => ({ ...prev, notes: open }))}
          >
            <Card>
              <CollapsibleTrigger asChild>
                <div className="hover:bg-muted/30 flex cursor-pointer items-center gap-2 p-4 transition-colors">
                  <div className="bg-primary/10 flex h-7 w-7 items-center justify-center rounded-md">
                    <StickyNote className="text-primary h-3.5 w-3.5" />
                  </div>
                  <span className="text-sm font-semibold">{t('new.notes')}</span>
                  {expandedSections.notes ? (
                    <ChevronDown className="text-muted-foreground ml-1 h-4 w-4" />
                  ) : (
                    <ChevronRight className="text-muted-foreground ml-1 h-4 w-4" />
                  )}
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <CardContent className="border-t p-4 pt-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <Field label={t('new.specialRequests')}>
                      <Textarea
                        {...register('specialRequests')}
                        placeholder={t('new.specialRequestsPlaceholder')}
                        rows={3}
                        className="resize-none"
                      />
                    </Field>
                    <Field label={t('new.internalNotes')}>
                      <Textarea
                        {...register('internalNotes')}
                        placeholder={t('new.internalNotesPlaceholder')}
                        rows={3}
                        className="resize-none"
                      />
                    </Field>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </form>

      {/* Modals */}
      <CustomerDrawer
        open={isCustomerDrawerOpen}
        onOpenChange={setIsCustomerDrawerOpen}
        onCustomerCreated={handleCustomerCreated}
      />

      <AllocationPickerModal
        open={isAllocationPickerOpen}
        onOpenChange={setIsAllocationPickerOpen}
        travelStartDate={watchedValues.travelStartDate}
        travelEndDate={watchedValues.travelEndDate}
        existingAllocationIds={selectedAllocations.map((a) => a.allocationId)}
        onSelect={handleAddAllocations}
      />

      {/* Spacer for sticky bar */}
      <div className="h-20" />

      {/* Sticky Bottom Action Bar — Expandable */}
      <div
        className={cn(
          'bg-background/95 supports-[backdrop-filter]:bg-background/80 fixed bottom-0 left-0 right-0 z-50 border-t backdrop-blur transition-all duration-300',
          isStickyExpanded && 'shadow-[0_-8px_30px_rgba(0,0,0,0.1)]'
        )}
      >
        {/* Expanded Panel */}
        {isStickyExpanded && (
          <div className="mx-auto max-w-screen-2xl border-b px-6 py-4">
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {/* Summary */}
              <div className="space-y-2 text-sm">
                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
                  {t('new.bookingSummary')}
                </p>
                {selectedCustomer && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">{t('new.customer')}</span>
                    <span className="font-medium">
                      {selectedCustomer.firstName} {selectedCustomer.lastName}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('new.travelDates')}</span>
                  <span className="font-medium tabular-nums">
                    {watchedValues.travelStartDate
                      ? format(new Date(watchedValues.travelStartDate), 'MMM d')
                      : '—'}{' '}
                    →{' '}
                    {watchedValues.travelEndDate
                      ? format(new Date(watchedValues.travelEndDate), 'MMM d')
                      : '—'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{t('new.travelers')}</span>
                  <span className="font-medium">
                    {totalAdults}A{totalChildren > 0 && ` ${totalChildren}C`}
                    {totalInfants > 0 && ` ${totalInfants}I`}
                  </span>
                </div>
                {itemSummaries.length > 0 && (
                  <div className="space-y-1.5 border-t pt-2">
                    {itemSummaries.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between text-xs">
                        <span>
                          {item.name
                            ? t(`items.serviceNames.${item.name}`, item.name)
                            : t(
                                `items.serviceTypes.${item.serviceType.toLowerCase()}`,
                                item.serviceType
                              )}{' '}
                          x{item.quantity}
                        </span>
                        <div className="flex items-center gap-3">
                          <span className="tabular-nums">
                            {item.currencySymbol}
                            {item.sell.toFixed(2)}
                          </span>
                          <span
                            className={cn(
                              'tabular-nums',
                              item.profit >= 0 ? 'text-emerald-600' : 'text-red-600'
                            )}
                          >
                            {item.profit >= 0 ? '+' : ''}
                            {item.currencySymbol}
                            {item.profit.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Initial Payment */}
              <div className="space-y-2">
                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
                  {t('new.initialPayment')}
                </p>
                <Field label={t('new.amount')}>
                  <Input
                    type="number"
                    {...register('initialPaymentAmount', { valueAsNumber: true })}
                    placeholder="0.00"
                    step="0.01"
                  />
                </Field>
                {(watchedValues.initialPaymentAmount ?? 0) > 0 && (
                  <Field label={t('new.paymentMethod')}>
                    <Combobox
                      options={[
                        { value: 'CASH', label: t('new.cash') },
                        { value: 'CREDIT_CARD', label: t('new.creditCard') },
                        { value: 'DEBIT_CARD', label: t('new.debitCard') },
                        { value: 'BANK_TRANSFER', label: t('new.bankTransfer') },
                        { value: 'PAYPAL', label: t('new.paypal') },
                        { value: 'OTHER', label: t('new.other') },
                      ]}
                      value={watchedValues.initialPaymentMethod || ''}
                      onValueChange={(value) =>
                        setValue(
                          'initialPaymentMethod',
                          value as BookingForm['initialPaymentMethod']
                        )
                      }
                      placeholder={t('new.selectMethod')}
                    />
                  </Field>
                )}
                {(watchedValues.initialPaymentAmount ?? 0) > 0 && (
                  <PaymentAccountSelect
                    value={watchedValues.initialPaymentAccountId}
                    onChange={(v) => setValue('initialPaymentAccountId', v)}
                  />
                )}
              </div>

              {/* Invoice Options */}
              <div className="space-y-2">
                <p className="text-muted-foreground text-xs font-semibold uppercase tracking-wider">
                  {t('new.invoiceOptions')}
                </p>
                <div className="flex items-center gap-2">
                  <Controller
                    control={control}
                    name="createInvoice"
                    render={({ field }) => (
                      <Checkbox
                        id="createInvoiceSB"
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                  <Label htmlFor="createInvoiceSB" className="cursor-pointer text-sm">
                    {t('new.createInvoiceAutomatically')}
                  </Label>
                </div>
                {watchedValues.createInvoice && (
                  <div className="bg-muted/30 space-y-2 rounded-lg border p-3">
                    <Field label={t('new.invoiceType')}>
                      <Controller
                        control={control}
                        name="invoiceType"
                        render={({ field }) => (
                          <Select value={field.value || 'PROFORMA'} onValueChange={field.onChange}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="PROFORMA">{t('new.proforma')}</SelectItem>
                              <SelectItem value="INVOICE">{t('new.invoice')}</SelectItem>
                              <SelectItem value="CREDIT_NOTE">{t('new.creditNote')}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                    </Field>
                    <Field label={t('new.invoiceDueDate')}>
                      <Input type="date" {...register('invoiceDueDate')} />
                    </Field>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Collapsed Bar — always visible */}
        <div className="mx-auto flex max-w-screen-2xl items-center justify-between gap-4 px-6 py-3">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                appendItem({
                  serviceType: 'ACCOMMODATION',
                  name: '',
                  quantity: 1,
                  unitCost: 0,
                  serviceFee: 0,
                  currency: 'TRY',
                  passengerIndexes: [],
                })
              }
            >
              <Plus className="mr-1.5 h-4 w-4" />
              {t('new.addItem')}
            </Button>
            {items.length > 0 && (
              <span className="text-muted-foreground text-sm">{items.length} kalem</span>
            )}
          </div>
          <div className="flex items-center gap-4">
            {/* Expand/Collapse toggle */}
            <button
              type="button"
              onClick={() => setIsStickyExpanded(!isStickyExpanded)}
              className="hover:bg-muted flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors"
            >
              <Receipt className="text-muted-foreground h-4 w-4" />
              <div className="text-right">
                <div className="text-muted-foreground text-xs">{t('new.grandTotal')}</div>
                <div className="text-sm font-bold tabular-nums">
                  ₺{grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </div>
              </div>
              {itemsProfitTotal !== 0 && (
                <span
                  className={cn(
                    'text-xs font-semibold tabular-nums',
                    itemsProfitTotal >= 0 ? 'text-emerald-600' : 'text-red-600'
                  )}
                >
                  {itemsProfitTotal >= 0 ? '+' : ''}₺{itemsProfitTotal.toFixed(2)}
                </span>
              )}
              <ChevronDown
                className={cn(
                  'text-muted-foreground h-4 w-4 transition-transform',
                  isStickyExpanded && 'rotate-180'
                )}
              />
            </button>

            <Button
              size="lg"
              onClick={handleSubmit(onSubmit)}
              disabled={createBookingMutation.isPending}
              className="min-w-[180px]"
            >
              {createBookingMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('new.createBooking')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
